export function isPimentoFailure(res: unknown): boolean {
  if (res instanceof Error) return true;
  if (
    res &&
    typeof res === "object" &&
    "error" in res &&
    (res as { error?: unknown }).error
  ) {
    return true;
  }
  return false;
}

export function getPimentoFailureMessage(
  res: unknown,
  fallback: string
): string {
  if (res instanceof Error) {
    return res.message || fallback;
  }
  if (res && typeof res === "object" && "error" in res) {
    const error = (res as { error?: unknown }).error;
    if (typeof error === "string" && error) return error;
  }
  return fallback;
}

function throwHttpError(
  options: { error: string },
  status: number,
  body: unknown
): never {
  const serverMessage =
    body &&
    typeof body === "object" &&
    "error" in body &&
    (body as { error?: unknown }).error
      ? String((body as { error: unknown }).error)
      : "(No error message from server)";
  const prefix = options.error || "";
  const statusPart = `${status} error: ${serverMessage}`;
  // Avoid "Failed to X 500 error: Failed to X" when the server reuses options.error
  throw new Error(
    prefix && !serverMessage.startsWith(prefix)
      ? `${prefix} ${statusPart}`
      : statusPart
  );
}

async function parsePimentoResponse(
  res: { status: number; response: Promise<unknown> },
  options: { error: string }
): Promise<unknown> {
  let body: unknown = null;
  try {
    body = await res.response;
  } catch {
    // Non-JSON error body (proxy HTML, redirect, empty response)
  }
  if (res.status >= 400) {
    throwHttpError(options, res.status, body);
  }
  return body;
}

export function PimentoPost(
  route: string,
  body?: any,
  options: { error: string } = { error: "" },
  signal?: AbortSignal
): Promise<any> {
  return (
    fetch(route, {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...body,
      }),
      signal,
    })
      // Pass HTTP status code and parsed response together
      // (there's no way to access the status after `res.json` otherwise)
      .then((res) => ({ status: res.status, response: res.json() }))
      .then((res) => parsePimentoResponse(res, options))
      .then((res: any) => {
        if (res?.error) {
          console.error(options.error, res.error);
        }
        return res;
      })
      .catch((err) => {
        console.error(err);
        return err;
      })
  );
}

export function PimentoPut(
  route: string,
  body?: any,
  options: { error: string } = { error: "" },
  signal?: AbortSignal
): Promise<any> {
  return (
    fetch(route, {
      method: "PUT",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...body,
      }),
      signal,
    })
      // Pass HTTP status code and parsed response together
      // (there's no way to access the status after `res.json` otherwise)
      .then((res) => ({ status: res.status, response: res.json() }))
      .then((res) => parsePimentoResponse(res, options))
      .then((res: any) => {
        if (res?.error) {
          console.error(options.error, res.error);
        }
        return res;
      })
      .catch((err) => {
        console.error(err);
        return err;
      })
  );
}

export function PimentoDelete(
  route: string,
  options: { error: string; body?: any } = { error: "" },
  signal?: AbortSignal
) {
  return (
    fetch(route, {
      method: "DELETE",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal,
    })
      // Pass HTTP status code and parsed response together
      // (there's no way to access the status after `res.json` otherwise)
      .then((res) => ({ status: res.status, response: res.json() }))
      .then((res) => parsePimentoResponse(res, options))
      .then((res: any) => {
        if (res?.error) {
          console.error(options.error, res.error);
        }
        return res;
      })
      .catch((err) => {
        console.error(err);
        return err;
      })
  );
}

export const buildErrorMessage = async (res: Response, fallback: string) => {
  try {
    const data = await res.clone().json();
    const detail = (data as any)?.error || (data as any)?.message;
    if (detail) {
      return `${fallback}: ${detail}`;
    }
  } catch {
    // ignore JSON parse errors and fall back to status text
  }
  return `${fallback}: ${res.status} ${res.statusText}`;
};
